/**
 * LegalPage — Reusable layout for legal/policy pages (privacy, terms, etc.).
 *
 * Renders markdown content with:
 * - SEO tags (title, description, canonical, OG, JSON-LD WebPage)
 * - Gradient page title + "Last updated" subtitle
 * - Styled markdown body via react-markdown
 *
 * Navbar, background, and footer are provided by PublicLayout.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Container, Text, Stack } from '@mantine/core';
import { SEO } from './SEO';

interface LegalPageProps {
  /** Page title shown as gradient heading and in SEO tags */
  title: string;
  /** Human-readable last-updated date, e.g. "February 16, 2026" */
  lastUpdated: string;
  /** Raw markdown string to render */
  content: string;
  /** Canonical path for SEO, e.g. "/privacy" */
  seoPath: string;
  /** Meta description for SEO */
  seoDescription: string;
}

const CSS = `
  .legal-content h2 {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0a0a0a;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0,0,0,.06);
  }
  .legal-content h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #334155;
    margin-top: 1.75rem;
    margin-bottom: 0.5rem;
  }
  .legal-content p {
    color: #64748b;
    line-height: 1.8;
    margin-bottom: 1rem;
  }
  .legal-content ul, .legal-content ol {
    color: #64748b;
    padding-left: 1.5rem;
    margin-bottom: 1rem;
  }
  .legal-content li {
    margin-bottom: 0.4rem;
    line-height: 1.7;
  }
  .legal-content a {
    color: #7c3aed;
    text-decoration: none;
    border-bottom: 1px solid rgba(124,58,237,.3);
    transition: border-color .2s;
  }
  .legal-content a:hover {
    border-bottom-color: #7c3aed;
  }
  .legal-content hr {
    border: none;
    border-top: 1px solid rgba(0,0,0,.06);
    margin: 2rem 0;
  }
  .legal-content strong {
    color: #0a0a0a;
    font-weight: 600;
  }
`;

const gradientText: React.CSSProperties = {
  background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export function LegalPage({
  title,
  lastUpdated,
  content,
  seoPath,
  seoDescription,
}: LegalPageProps) {
  return (
    <Box style={{ position: 'relative', zIndex: 1 }}>
      <SEO
        title={title}
        description={seoDescription}
        path={seoPath}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': `${title} | Manifestus`,
          'url': `https://manifestus.ai${seoPath}`,
          'description': seoDescription,
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Manifestus',
            'url': 'https://manifestus.ai',
          },
        }}
      />
      <style>{CSS}</style>

      <Container size="md" pt={120} pb={80}>
        <Stack gap="xs" mb={40}>
          <Text component="h1" fw={800} fz={36} style={gradientText}>
            {title}
          </Text>
          <Text size="sm" c="dimmed" style={{ opacity: 0.5 }}>
            Last updated: {lastUpdated}
          </Text>
        </Stack>

        <Box className="legal-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </Box>
      </Container>
    </Box>
  );
}
